import React, { useState, useEffect, useContext, useRef } from "react";
import queryString from "query-string";
import { useHistory, Link } from "react-router-dom";

import { Grid, IconButton, Divider, Button, Hidden, Fab } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import GroupIcon from "@material-ui/icons/Group";
import AddIcon from "@material-ui/icons/Add";
import PublishIcon from "@material-ui/icons/Publish";

import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import { getUsers } from "../api/UserAPI";
import { getUsers as getTenantUsers, importUsers } from "../api/TenantUserAPI";
import { getUsers as getCustomerUsers } from "../api/CustomerUserAPI";
import { Users, User, UsersQueryParams, UserImportSummaryResponse } from "../model/User";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import ValidatedInput from "../components/ValidatedInput";
import { RouterParams } from "../router/RouterParams";
import UserMenu from "./UserMenu";
import UserActivatePopup from "./UserActivatePopup";
import UserBlockPopup from "./UserBlockPopup";
import UserUnblockPopup from "./UserUnblockPopup";
import UserDeletePopup from "./UserDeletePopup";
import { ErrorSnackbar, SuccessSnackbar } from "../components/Snackbars";
import NumberFormat from "react-number-format";
import { phoneFormat } from "../components/Validators";

import Gridable from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import UsersImportSummaryPopup from "./UsersImportSummaryPopup";
import UsersMenu from "./UsersMenu";

export default function UsersList({ match }: RouterParams) {
    const context = useContext(AppContext);
    const history = useHistory();
    const qs = queryString.parse(window.location.search);
    const paramsFromQueryString = (): UsersQueryParams => {
        return {
            "search": typeof qs["search"] === "string" ? qs["search"] as string : "",
            "role_id": typeof qs["role_id"] === "string" ? qs["role_id"] as string : "",
            "tenant_id": match.params.tenantId ?? "",
        } as UsersQueryParams;
    };

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Users | undefined>();
    const [params, setParams] = useState<UsersQueryParams>(paramsFromQueryString);
    const [workingParams, setWorkingParams] = useState<UsersQueryParams>(paramsFromQueryString);
    const [roleNames, setRoleNames] = useState<string[]>([]);
    const [roleIds, setRoleIds] = useState<string[]>([]);

    const [user, setUser] = useState<User>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [activatePopup, setActivatePopup] = useState<boolean>(false);
    const [dialog, setDialog] = useState<"block" | "unblock" | "delete">();

    const [success, setSuccess] = useState<string>();
    const [error, setError] = useState<string>();
    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    const fileInput = useRef<HTMLInputElement>(null);
    const [gridAnchorEl, setGridAnchorEl] = useState<null | HTMLElement>(null);
    const [uploading, setUploading] = useState(false);
    const [importResponse, setImportResponse] = useState<UserImportSummaryResponse>();

    const retrievePromise = (): Promise<Users> => {
        let offset = getOffset(page, pageSize);
        let tenantId = context.session?.tenant?.id ?? match.params.tenantId;
        if (tenantId) {
            if (match.params.customerId) {
                return getCustomerUsers(tenantId, match.params.customerId, pageSize, offset, params);
            }
            if (context.isGranted("CustomersUsersRead") && context.session && context.session.customer) {
                return getCustomerUsers(tenantId, context.session.customer.id, pageSize, offset, params)
            }
            return getTenantUsers(tenantId, pageSize, offset, params)
        }
        return getUsers(pageSize, offset, params);
    };

    const load = () => {
        setStatus("loading");
        retrievePromise().then((response) => {
            setData(response);
            setRoleIds(response.metadata.roles.map((role) => role.id));
            setRoleNames(response.metadata.roles.map((role) => translate(`users.roles.${role.id}`) as string));
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    };

    // eslint-disable-next-line
    useEffect(load, [context.session, page, pageSize, params]);

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let qs = queryString.parse(window.location.search);
        qs["search"] = workingParams.search;
        qs["role_id"] = workingParams.role_id;
        qs["page"] = "1";

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);

        setParams(workingParams);
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onClickedOptions = (user: User) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setUser(user);
    };

    const showDialog = (dialog: "block" | "unblock" | "delete") => () => {
        if (!user) return;
        setAnchorEl(null);
        setDialog(dialog);
    };

    const onBlocked = () => {
        if (!user) return;

        user.blocked = true;
        setSuccess(translate("users.block.success", {
            "first_name": user.first_name,
            "last_name": user.last_name,
            "email": user.email
        }) as string);
        setUser(undefined);
        setDialog(undefined);
    };

    const onUnblocked = () => {
        if (!user) return;

        user.blocked = false;
        setSuccess(translate("users.unblock.success", {
            "first_name": user.first_name,
            "last_name": user.last_name,
            "email": user.email
        }) as string);
        setUser(undefined);
        setDialog(undefined);
    };

    const onDelete = () => {
        if (!user) return;

        load();
        setSuccess(translate("users.delete.success", {
            "first_name": user.first_name,
            "last_name": user.last_name,
            "email": user.email
        }) as string);
        setUser(undefined);
        setDialog(undefined);
    };

    const onError = (error: string) => {
        setError(error);
        setUser(undefined);
        setDialog(undefined);
    };

    const onCancel = () => {
        setUser(undefined);
        setDialog(undefined);
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setUser(undefined);
    };

    const showActivatePopup = () => {
        setAnchorEl(null);
        setActivatePopup(true);
    };

    const hideActivatePopup = () => {
        setActivatePopup(false);
        setUser(undefined);
    };

    const onClosedSnackbar = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    const openFileImport = () => {
        fileInput.current!.click();
        setGridAnchorEl(null);
    }

    const onCloseImportDialog = () => {
        setImportResponse(undefined);
        setGridAnchorEl(null);
        fileInput.current!.value = "";
    };

    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }

        setUploading(true);
        importUsers(context.session!.tenant!.id, event.target.files[0]).then((response) => {
            load();
            setImportResponse(response);
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setUploading(false);
        });
    }

    const onClickedMore = (event: React.MouseEvent<HTMLElement>) => {
        setGridAnchorEl(event.currentTarget);
    };

    return (
        <Pagination title={translate("users.title")}
            subtitle={data?.tenant ? data.tenant.name : (data?.metadata.customer ? data.metadata.customer.name : undefined)}
            icon={<GroupIcon />}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            backButton={!!match.params.customerId} backTo={match.params.customerId ? `/customers/${match.params.customerId}` : undefined}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize} action={context.isGrantedAny(["UsersCreate", "TenantsUsersCreate", "CustomersUsersCreate"]) ?
                (<Grid container alignItems="center" justify="flex-end" spacing={1}>
                    <Grid item xs="auto">
                        <Link to={match.params.tenantId ? `/tenants/${match.params.tenantId}/users/new` : (match.params.customerId ? `/customers/${match.params.customerId}/users/new` : context.isGranted("CustomersUsersCreate") ? `/customers/${context.session?.customer?.id}/users/new` : "/users/new")}>
                            <Fab color="primary" size="small" title={translate("buttons.add") as string}>
                                <AddIcon />
                            </Fab>
                        </Link>
                    </Grid>
                    {context.isGranted("TenantsUsersCreate") && (
                        <Grid item xs="auto">
                            <Hidden xsDown implementation="css">
                                <Fab color="secondary" size="small" title={translate("buttons.import_users") as string} onClick={openFileImport}>
                                    <PublishIcon />
                                </Fab>
                            </Hidden>
                        </Grid>
                    )}
                    {context.isGranted("TenantsUsersCreate") && (
                        <Grid item xs="auto">
                            <IconButton color="default" size="small" onClick={onClickedMore} disabled={uploading}>
                                <MoreVertIcon />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>) : undefined
            }>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={12} md>
                        <ValidatedInput type="text" id="search" name="search" label={translate("users.filter") as string}
                            margin="dense" disabled={false}
                            value={workingParams.search} onValueChanged={onFilterChanged} />
                    </Grid>
                    <Grid item xs md="auto">
                        <ValidatedInput type="text" id="role_id" name="role_id" label={translate("users.roles.title") as string}
                            margin="dense" disabled={false}
                            value={workingParams.role_id} onValueChanged={onFilterChanged}
                            options={roleIds}
                            optionLabels={roleNames}
                            emptyOption={translate("users.roles.all") as string} />
                    </Grid>
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("users.empty") as string}
                columns={[
                    {
                        title: translate("users.name") as string,
                        converter: (user) => (
                            <div>
                                <Ellipsis text={`${user.first_name} ${user.last_name}`} lenght={0} uppercased={false} />
                                <Hidden smUp>
                                    <br></br>
                                    <Ellipsis text={user.email} lenght={0} uppercased={false} secondary />
                                    <br></br>
                                    <Ellipsis text={user.role ? translate(`users.roles.${user.role.id}`) as string : "---"} lenght={0} uppercased={false} secondary />
                                </Hidden>
                            </div>
                        ),
                        xs: true
                    },
                    {
                        title: translate("users.email") as string,
                        converter: (user) => user.email,
                        xs: false,
                        sm: 3,
                        lg: 3
                    },
                    {
                        title: translate("users.phone") as string,
                        converter: (contact) => (contact.phone ? <NumberFormat value={contact.phone} format={phoneFormat(contact.phone)} displayType="text" /> : "---"),
                        xs: false,
                        sm: false,
                        md: 2,
                        lg: 2,
                        xl: 2
                    },
                    {
                        title: translate("users.roles.title") as string,
                        converter: (user) => user.role ? translate(`users.roles.${user.role.id}`) : "---",
                        xs: false,
                        sm: 2,
                        lg: 2
                    },
                    {
                        title: translate("users.status.title") as string,
                        converter: (user) => translate(`users.status.${(user.blocked ? "blocked" : user.signup_code ? "inactive" : "active")}`),
                        xs: false,
                        sm: 2,
                        lg: 1
                    },
                    {
                        title: (
                            <IconButton size="small" style={{ "visibility": "hidden" }} disabled><MoreVertIcon /></IconButton>
                        ),
                        converter: (user) => (
                            <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(user)}>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        justify: "flex-end",
                        xs: "auto"
                    }
                ]} />
            <input type="file" onChange={handleUpload} ref={fileInput} style={{ display: "none" }} accept=".xlsx, .csv, .xls" />
            {user && anchorEl &&
                <UserMenu user={user}
                    tenantId={match.params.tenantId}
                    customerId={match.params.customerId}
                    anchor={anchorEl}
                    onClose={onCloseOption}
                    onBlock={showDialog("block")}
                    onUnblock={showDialog("unblock")}
                    onDelete={showDialog("delete")}
                    onActivated={showActivatePopup} />
            }
            {user && activatePopup &&
                <UserActivatePopup user={user} onClose={hideActivatePopup} />
            }
            {user && dialog === "block" &&
                <UserBlockPopup user={user} tenantId={context.session?.tenant?.id ?? match.params.tenantId}
                    onCompleted={onBlocked}
                    onError={onError}
                    onCancelled={onCancel} />
            }
            {user && dialog === "unblock" &&
                <UserUnblockPopup user={user} tenantId={context.session?.tenant?.id ?? match.params.tenantId}
                    onCompleted={onUnblocked}
                    onError={onError}
                    onCancelled={onCancel} />
            }
            {user && dialog === "delete" &&
                <UserDeletePopup user={user}
                    tenantId={context.session?.tenant?.id ?? match.params.tenantId}
                    customerId={context.session?.customer?.id ?? match.params.customerId}
                    onCompleted={onDelete}
                    onError={onError}
                    onCancelled={onCancel} />
            }
            {importResponse && (
                <UsersImportSummaryPopup data={importResponse} onClose={onCloseImportDialog} />
            )}
            {gridAnchorEl && (
                <UsersMenu anchor={gridAnchorEl}
                    onClose={() => setGridAnchorEl(null)}
                    onImportCustomers={openFileImport} />
            )}
            <ErrorSnackbar message={error} onClose={onClosedSnackbar} />
            <SuccessSnackbar message={success} onClose={onClosedSnackbar} />
        </Pagination>
    );
}